// pgw 07-dec-2025 includes changes

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { fetchSetlist, packSetlist } from './setlistAPI';
import initialState from './initialState';
import initialSessionState from '../session/initialState';

import { unpackSession, unpackSetlist, packSession } from "../session/sessionAPI";
import { removeAssetFromCache } from '../../services/assetStore';
import {createSetlistZip} from '../../factories/Zipper';
import { triggerBlobDownload } from '../../factories/ui';
import {addNotificationAsync} from '../ui/uiSlice';

export const unpackSessionAsync = createAsyncThunk("session/unpack", async (payload, thunkAPI) => {
  const sessionObject = await unpackSession(payload);

  return sessionObject;
});

export const unpackSetlistAsync = createAsyncThunk("session/unpackSetlist", async (payload, thunkAPI) => {
  const setlistObject = await unpackSetlist(payload);



  const setlistSessions = [];

  for (const setlistKey of Object.getOwnPropertyNames(setlistObject)) {
    const sessionBlob = setlistObject[setlistKey];
    const sessionObject = await unpackSession(sessionBlob);
    setlistSessions.push(sessionObject);
  }

  return setlistSessions;
});

export const packSetlistAsync = createAsyncThunk("session/packSetlist", async (payload, thunkAPI) => {
  
  // get setlist session from state
  // filter out all containing userAudio stems
  const sessions = thunkAPI.getState().setlist.sessions.filter(s => s.userStems.length === 0);

  // add all session zips into one master .setlist zip file
  const setListBlob = await packSetlist(sessions);

   // prompt download in browser 👍
  const dateNow = new Date();
  const setlistFilename = `mySetlist-${dateNow.toLocaleDateString().replaceAll('/','')}_${dateNow.toLocaleTimeString().replaceAll(':','')}.setlist`;
  triggerBlobDownload(setListBlob, setlistFilename);


  thunkAPI.dispatch(addNotificationAsync({
    title: 'Setlist saved',
    text: `${setlistFilename} was downloaded`,
    type: 'positive',
}))
  return true;
});

export const removeSessionFromSetlistAsync = createAsyncThunk("setlsit/remove", async (payload, thunkAPI) => {
  // remove associated items from cache
  const session = thunkAPI.getState().setlist.sessions.find(s =>s.id == payload);
  for (const userStem of session.userStems) {
    await removeAssetFromCache(userStem.id);
  }
  //remove associated song zip
  await removeAssetFromCache(session.song.pathzip);

  return payload;
});

// pgw 30-jan-2026
const mergeStemSettings = (baseStems, savedStems) => {
  if (!Array.isArray(baseStems) || baseStems.length === 0) return baseStems;
  if (!Array.isArray(savedStems) || savedStems.length === 0) return baseStems;

  const savedById = Object.fromEntries(savedStems.map(s => [s.id, s]));

  return baseStems.map(base => {
    const saved = savedById[base.id];
    if (!saved) return base;

    // IMPORTANT: spread base first so base.index wins
    return {
      ...base,
      level: saved.level ?? base.level,
      muted: saved.muted ?? base.muted,
      soloed: saved.soloed ?? base.soloed,
      pan: saved.pan ?? base.pan,
      // (anything else you persist per stem)
    };
  });
};


export const setlistSlice = createSlice({
  name: 'setlist',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    createEmptySession: (state, action) => {

	  console.log("[FEATURES_SETLIST_SETLISTSLICE] createEmptySession state = ", state);
	  console.log("[FEATURES_SETLIST_SETLISTSLICE] createEmptySession action = ", action);
	  console.log("[FEATURES_SETLIST_SETLISTSLICE] createEmptySession calling const newSession ");



      const newSession = {
        ...initialSessionState,
        id: uuidv4().slice(0,8),
        title: action.payload.title,
        createdAt: new Date().toISOString(),
        song: {
          mix: action.payload.title,
          artist: '',
          len: 360,
          stems: [],
          userStems: []
        }
      };

	  console.log("[FEATURES_SETLIST_SETLISTSLICE] newSession 1", {
		id: newSession.id,
		songId: newSession.song?.songId,
		stems: newSession.stems?.map(s => ({ id: s.id, level: s.level })),
	  });

	  state.lastCreatedSession = newSession.id;
	  console.log("[FEATURES_SETLIST_SETLISTSLICE] lastCreatedSession set to ", state.lastCreatedSession);

      state.sessions.push(newSession);
    },

	// pgw 29-jan-2026
    createSessionFromSong: (state, action) => {

		console.log("[FEATURES_SETLIST_SETLISTSLICE] createSessionFromSong reducer hit: setlistSlice",action.type);
		console.log("[CREATE SESSION] incoming payload", {
		  songId: action.payload?.songId || action.payload?.song?.songId,
		  hasRestoreSettings: !!action.payload?.restoreSettings,
		  restoreStemCount: action.payload?.restoreSettings?.stems?.length,
		  restoreStemPreview: action.payload?.restoreSettings?.stems?.slice?.(0, 5),
		});




	  	const song = action?.payload?.song;

		if (!song) {
			console.warn("[FEATURES_SETLIST_SETLISTSLICE] createSessionFromSong called with no song", action?.payload);
			return;
		}





const stemsFromSong = song?.stems || [];

const restoreSettings = action.payload?.restoreSettings || null;
const restoreStems = restoreSettings?.stems || [];
const restoreByIndex = new Map(restoreStems.map(r => [r.index, r]));

const stemsToUse = stemsFromSong.map(stem => {
  const restored = restoreByIndex.get(stem.index);
  if (!restored) return stem;

  return {
    ...stem,
    level: restored.level ?? stem.level,
    muted: restored.muted ?? stem.muted,
    soloed: restored.soloed ?? stem.soloed,
    pan: restored.pan ?? stem.pan,
  };
});

// Convert persisted numbers back to the session object shape
const restoredSpeedValue =
  typeof restoreSettings?.speed === "number" ? restoreSettings.speed : null;

const restoredPitchValue =
  typeof restoreSettings?.pitch === "number" ? restoreSettings.pitch : null;

const restoredSpeed =
  restoredSpeedValue == null
    ? initialSessionState.speed
    : { value: restoredSpeedValue, displayValue: `${(restoredSpeedValue * 100).toFixed(0)}%` };

const restoredPitch =
  restoredPitchValue == null
    ? initialSessionState.pitch
    : {
        value: restoredPitchValue,
        displayValue:
          restoredPitchValue === 0
            ? "0"
            : `${restoredPitchValue > 0 ? "+" : ""}${restoredPitchValue / 100}st`,
      };



console.log("[CREATE SESSION] restore result", {
  hasRestoreSettings: !!restoreSettings,
  restoreCount: restoreStems.length,
  stemsFromSong: stemsFromSong.map(s => ({ index: s.index, level: s.level })),
  stemsToUse: stemsToUse.map(s => ({ index: s.index, level: s.level })),
  restoredSpeed,
  restoredPitch,
});




		  console.log("[FEATURES_SETLIST_SETLISTSLICE] createSessionFromSong 1", {
			title: action.payload.title,
			stems: song.stems?.map(s => ({
			  id: s.id,
			  level: s.level,
			  muted: s.muted,
			  soloed: s.soloed
			}))
		  });





	  console.log("[CREATE SESSION] restore apply", {
		hasRestoreSettings: !!restoreSettings,
		restoreCount: restoreStems.length,
		stemsFromSong: stemsFromSong.map(s => ({ index: s.index, level: s.level })),
		stemsToUse: stemsToUse.map(s => ({ index: s.index, level: s.level })),
	  });


	  let didCreate = false;

	  if (action?.payload?.song?.mix) {

		    console.log("[FEATURES_SETLIST_SETLISTSLICE]  createSessionFromSong 3 action?.payload?.song?.mix = ", action?.payload?.song?.mix)
		  	console.log("[FEATURES_SETLIST_SETLISTSLICE] createSessionFromSong 4 Test Song - new logic; calling const newSession");

			const newSession = {
			  ...initialSessionState,
			  id: uuidv4().slice(0, 8),
			  title: action.payload.title,
			  song,

			  // stems will be populated later during mount
			  stems: song?.stems || [],

			  // keep restore settings around so mount can apply them
			  restoreSettings: action.payload?.restoreSettings || null,

			  speed: restoredSpeed,
			  pitch: restoredPitch,
			  masterMute: restoreSettings?.masterMute ?? initialSessionState.masterMute,

			  origin: action.payload.origin || null,
			  createdAt: new Date().toISOString(),
			  lastUpdatedAt: new Date().toISOString(),
			};

			/*
			const newSession = {
			  ...initialSessionState,
			  id: uuidv4().slice(0, 8),
			  title: action.payload.title,
			  song,

			  stems: stemsToUse,

			  speed: restoredSpeed,
			  pitch: restoredPitch,
			  masterMute: restoreSettings?.masterMute ?? initialSessionState.masterMute,


			  origin: action.payload.origin || null,

			  createdAt: new Date().toISOString(),
			  lastUpdatedAt: new Date().toISOString(),
			};
			*/

			console.log("[FEATURES_SETLIST_SETLISTSLICE] newSession 2 ", {
			  id: newSession.id,
			  songId: newSession.song?.songId,
			  stems: newSession.stems?.map(s => ({ id: s.id, level: s.level })),
			});

			  console.log("[FEATURES_SETLIST_SETLISTSLICE] createSessionFromSong 5 newSession = ", newSession);


			state.lastCreatedSession = newSession.id;

			  console.log("[FEATURES_SETLIST_SETLISTSLICE] lastCreatedSession set to ", state.lastCreatedSession);

			state.sessions.push(newSession);

			didCreate = true;

	  }
	  else {
		  console.log("[FEATURES_SETLIST_SETLISTSLICE]  - calling saveExistingSession - no song " )
	  }


	 if (didCreate == false) {
	   console.log("[FEATURES_SETLIST_SETLISTSLICE]  - didCreate false: revert to existing logic ; calling const newSession " )
	   const newSession = {
		 ...initialSessionState,
		 id: uuidv4().slice(0,8),
		 title: action.payload.title,
		 song,

		 stems: stemsToUse,

		 speed: restoredSpeed,
		 pitch: restoredPitch,
		 masterMute: restoreSettings?.masterMute ?? initialSessionState.masterMute,

		 origin: action.payload.origin || null,

		 createdAt: new Date().toISOString(),
		 lastUpdatedAt: new Date().toISOString()
	   };
	  console.log("[FEATURES_SETLIST_SETLISTSLICE] newSession 3", {
		id: newSession.id,
		songId: newSession.song?.songId,
		stems: newSession.stems?.map(s => ({ id: s.id, level: s.level })),
	  });

	   state.lastCreatedSession = newSession.id;
	   state.sessions.push(newSession);
	 }

    },






    resetLastCreated: (state) => {
      state.lastCreatedSession = null;
    },
    addSongToMySetlist: (state, action) => {
      state.sessions.push(action.payload);
    },

    saveExistingSession: (state, action) => {

		// pgw 07-dec-2025

	  console.log("[FEATURES_SETLIST_SETLISTSLICE] setlistSlice saveExistingSession CALLED with session:", {
		id: action.payload.id,
		stems: action.payload.stems?.map(s => ({
		  id: s.id,
		  level: s.level,
		  muted: s.muted,
		  soloed: s.soloed
		}))
	  });


      state.sessions = state.sessions.filter(s=> s.id !== action.payload.id);

		/*
      state.sessions = [...state.sessions, {
        ...action.payload, 
        lastUpdatedAt:new Date().toISOString()
      }];
		*/

	  const updated = {
		...action.payload,
		lastUpdatedAt:new Date().toISOString()
	  };

	  console.log("[FEATURES_SETLIST_SETLISTSLICE] setlistSlice saveExistingSession WRITING", updated);

	  state.sessions = [...state.sessions, updated];




    }
    // // Use the PayloadAction type to declare the contents of `action.payload`
    // incrementByAmount: (state, action) => {
    //   state.value += action.payload;
    // },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(removeSessionFromSetlistAsync.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(s => s.id !== action.payload);
      })
      .addCase(packSetlistAsync.pending, (state, action) => {
   
      })
      .addCase(packSetlistAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(packSetlistAsync.fulfilled, (state, action) => {
      })
      .addCase(unpackSessionAsync.pending, (state, action) => {
        state.status = "unpacking";
        state.loadingProgress = 7;
      })
      .addCase(unpackSessionAsync.rejected, (state, action) => {
        state.status = "error";
        console.error(action);
      })
      .addCase(unpackSessionAsync.fulfilled, (state, action) => {
        Object.getOwnPropertyNames(action.payload).forEach(propertyName => {
          state[propertyName] = action.payload[propertyName];
        });
        const newSession = {
          ...action.payload,
          id: uuidv4().slice(0,8),
          imported: true,
          importedAt: new Date().toISOString()
        };
        state.sessions.push(newSession);
        state.status = "ready";
        state.loadingProgress = 10;
      })
      .addCase(unpackSetlistAsync.fulfilled, (state, action) => {

        const sessionPack = [];

        for (const session of action.payload) {
          const importedSession = {};
          Object.getOwnPropertyNames(session).forEach(propertyName => {
            importedSession[propertyName] = session[propertyName];
          });
          const newSession = {
            ...importedSession,
            id: uuidv4().slice(0,8),
            imported: true,
            importedAt: new Date().toISOString()
          };
          sessionPack.push(newSession);
        } 
        state.sessions = [...state.sessions, ...sessionPack];
      })
      
      ;
  },
});

export const { addSongToMySetlist, createEmptySession, createSessionFromSong, saveExistingSession, resetLastCreated } = setlistSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectSetlistSessions = (state) => state.setlist.sessions;
export const selectLastCreatedSession = (state) => state.setlist.lastCreatedSession;
// // We can also write thunks by hand, which may contain both sync and async logic.
// // Here's an example of conditionally dispatching actions based on current state.
// export const incrementIfOdd = (amount) => (dispatch, getState) => {
//   const currentValue = selectCount(getState());
//   if (currentValue % 2 === 1) {
//     dispatch(incrementByAmount(amount));
//   }
// };

export default setlistSlice.reducer;
